<template>
  <div
    v-if="!isEditing"
    :class="$style.editableItemContainer"
  >
    <component
      :is="component"
      v-if="component"
      :bind="editableComponentProps"
      :class="$style.component"
      :item="item"
    />
    <div
      v-else
      :class="$style.editableInputContent"
    >
      <slot />
    </div>
    <PencilIcon
      v-if="isEditable"
      :class="$style.editIcon"
      @click.stop="enterEditState"
    />
  </div>
  <div
    v-else
    :class="[$style.editFieldContainer, editViewClassName]"
    @click.stop=""
  >
    <InputValidator
      ref="inputValidator"
      :class="$style.input"
      :config="inputValidatorConfig"
    />
    <p
      :class="$style.cancelText"
      @mousedown="handleCancel"
    >
      cancel
    </p>
  </div>
</template>

<script>
import InputValidator from 'components/FormValidation/InputValidator.vue';
import PencilIcon from 'public/img/icons/pencil.svg?inline';

/**
 * @todo Consider using v-show instead of v-if/v-else which may avoid the use of $nextTick(), TBD.
 */
export default {
  name: 'EditableInputWithIcon',

  components: {
    InputValidator,
    PencilIcon,
  },

  inject: ['$validator'],

  props: {
    attribute: {
      required: true,
      type: String,
    },
    component: {
      default: null,
      type: Object,
    },
    config: {
      default: () => ({}),
      type: Object,
    },
    editViewClassName: {
      default: '',
      type: String,
    },
    editableComponentProps: {
      default: () => ({}),
      type: Object,
    },
    handleSubmit: {
      default: () => {},
      type: Function,
    },
    isEditableFunction: {
      type: Function,
      default: () => true,
    },
    item: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      newValue: '',
      isEditing: false,
    };
  },

  computed: {
    inputValidatorConfig() {
      return {
        attribute: this.attribute,
        dataLpignore: true,
        name: this.attribute,
        value: this.item[this.attribute],
        ...this.config,
      };
    },

    isEditable() {
      return !!(this.item && this.isEditableFunction(this.item));
    },
  },

  mounted() {
    this.newValue = this.item[this.attribute];
  },

  methods: {
    /**
     * And because the event handlers are removed dynamically they need to be added dynamically so
     * that behavior isn't lost when toggling to, from, and then back to the edit view.
     */
    addEventHandlers() {
      this.$refs.inputValidator.$on('InputValidatorBlur', this.handleBlur);
      this.$refs.inputValidator.$on('InputValidatorEnter', this.handleEnter);
      this.$refs.inputValidator.$on('InputValidatorModel', this.handleInput);
    },

    /**
     * When entering the edit state we first toggle to the edit view, which renders the
     * InputValidator, and _then_ in the next DOM update cycle the event handlers are bound
     * dynamically. This is necessary because the ref won't be set until _after_ the editing
     * state is entered.
     */
    enterEditState() {
      this.isEditing = true;
      this.$nextTick().then(() => this.addEventHandlers());
    },

    handleBlur(value) {
      this.saveChanges(value);
    },

    handleCancel() {
      this.newValue = this.item[this.attribute];
      this.leaveEditState();
    },

    handleEnter(value) {
      this.saveChanges(value);
    },

    /**
     * Changes are not saved until the InputValidator either looses focus (blur) or the enter key
     * is pressed. These two actions constitute _saving_ the changes.
     */
    handleInput(value) {
      this.newValue = value;
    },

    /**
     * When leaving the edit state event handlers are removed _first_ and _then_ in the next DOM
     * update cycle the state is toggled back to the not editing state. This is done to avoid any
     * state logic that might be necessary to _avoid_ handling blur events which are fired when the
     * InputValidator is removed from the DOM.
     */
    leaveEditState() {
      this.removeEventHandlers();
      this.$nextTick().then(() => { this.isEditing = false; });
    },

    removeEventHandlers() {
      this.$refs.inputValidator.$off('InputValidatorBlur');
      this.$refs.inputValidator.$off('InputValidatorEnter');
      this.$refs.inputValidator.$off('InputValidatorModel');
    },

    async saveChanges(newValue) {
      await this.$validator.validate(this.inputValidatorConfig.name);

      if (this.v2Errors.items.length === 0) {
        // handleSubmit() may or may not be asynchronous. By using await here we allow the
        // caller this option. The await operator can be used with an expression that does not
        // return a  Promise. In this case, the return value of the expression will be
        // converted to a resolved Promise.
        // SEE: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/await
        await this.handleSubmit(this.item, newValue);

        this.newValue = '';
        this.leaveEditState();
      }
    },
  },
};
</script>

<style scoped module>
@value (greyDark4, greyLight4) from '~css/colors.css';
@value (
  panelSpeed,
  panelTimingFunction
) from '~css/transitions.css';

.cancelText {
  align-self: center;
  cursor: pointer;
  font-size: 0.8em;
  margin-left: 1em;
  text-decoration: underline;
}

.component,
.editableInputContent {
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.component {
  max-width: 750px !important;
}

.editableInputContent {
  display: inline-block;
}

.editIcon {
  cursor: pointer;
  height: var(--iconHeight, 100%);
  margin: var(--iconTopOffset, 0) 0 0 var(--iconLeftOffset, 0);
  max-width: var(--iconMaxWidth, auto);
  min-width: 1.25em;
}

.editIcon:hover {
  color: var(--jcPrimary);
  transition: background-color panelSpeed panelTimingFunction;
}

.editableItemContainer {
  align-items: center;
  display: flex;
  height: 50px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.editFieldContainer {
  display: flex;
  padding-bottom: 0;
  padding-top: 0;
  position: relative;
  top: 6px;
}

.input {
  width: 100%;
}

.nameNotSet {
  color: greyLight4;
  font-size: 14px;
}
</style>
